<template>
  <div class="h-screen flex w-full vx-row no-gutter items-center justify-center" id="page-oauth-token">
    <div class="vx-col sm:w-1/2 md:w-1/2 lg:w-1/2 xl:w-1/3 sm:m-0 m-4">
      <vx-card>
        <div class="text-center p-8">
          <div v-if="loading">
            <vs-progress indeterminate color="primary"></vs-progress>
            <h4 class="mt-4">Processing token exchange...</h4>
          </div>

          <div v-else-if="response">
            <div v-if="response.access_token" class="success-response">
              <feather-icon icon="CheckCircleIcon" svgClasses="h-16 w-16 text-success mb-4" />
              <h4 class="mb-4 text-success">Token Exchange Successful</h4>
              <p class="mb-4">Your access token has been generated.</p>

              <div class="text-left bg-gray-100 p-4 rounded mb-4" style="font-family: monospace; font-size: 12px; word-break: break-all;">
                <strong>Response:</strong>
                <pre>{{ JSON.stringify(response, null, 2) }}</pre>
              </div>

              <vs-button @click="copyToken">Copy Access Token</vs-button>
            </div>

            <div v-else class="error-response">
              <feather-icon icon="AlertCircleIcon" svgClasses="h-16 w-16 text-danger mb-4" />
              <h4 class="mb-4 text-danger">Token Exchange Failed</h4>
              <p class="mb-2"><strong>Error:</strong> {{ response.error }}</p>
              <p class="text-sm text-gray-600">{{ response.error_description }}</p>
            </div>
          </div>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
import { exchangeOAuthToken } from '@/http/oauth/tokenExchange.js'

export default {
  name: 'OAuthToken',
  data() {
    return {
      loading: true,
      response: null,
    }
  },
  mounted() {
    this.handleTokenExchange()
  },
  methods: {
    async handleTokenExchange() {
      try {
        // Get request data from query params or POST data
        const requestData = this.getRequestData()

        if (!requestData) {
          this.response = {
            error: 'invalid_request',
            error_description: 'Missing required parameters. Expected: code, code_verifier, grant_type',
            status: 400,
          }
          this.loading = false
          return
        }

        // Exchange the token using the utility
        this.response = await exchangeOAuthToken(requestData)
        this.loading = false

        // If successful, also store in sessionStorage for programmatic access
        if (this.response.access_token) {
          sessionStorage.setItem('oauth_token_response', JSON.stringify({
            success: true,
            data: this.response,
          }))
        }
      } catch (error) {
        console.error('Token exchange error:', error)
        this.response = {
          error: 'server_error',
          error_description: 'An unexpected error occurred during token exchange',
          status: 500,
        }
        this.loading = false
      }
    },

    getRequestData() {
      const query = this.$route.query

      // Check query parameters first
      if (query.code && query.code_verifier) {
        return {
          code: query.code,
          code_verifier: query.code_verifier,
          grant_type: query.grant_type || 'authorization_code',
          client_id: query.client_id,
          redirect_uri: query.redirect_uri,
        }
      }

      // Check sessionStorage for POST-like data
      const pendingExchange = sessionStorage.getItem('oauth_token_exchange')
      if (pendingExchange) {
        sessionStorage.removeItem('oauth_token_exchange')
        return JSON.parse(pendingExchange)
      }

      return null
    },

    copyToken() {
      if (this.response && this.response.access_token) {
        navigator.clipboard.writeText(this.response.access_token).then(() => {
          this.$vs.notify({
            title: 'Copied!',
            text: 'Access token copied to clipboard',
            iconPack: 'feather',
            icon: 'icon-check',
            color: 'success',
          })
        })
      }
    },
  },
}
</script>

<style lang="scss">
#page-oauth-token {
  .success-response,
  .error-response {
    animation: fadeIn 0.3s ease-in;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}
</style>
